import { AfterViewInit, ComponentRef, Directive, ElementRef, Input, OnDestroy, ViewContainerRef } from '@angular/core';
import { CustomTooltipComponent } from '../components/custom-components/custom-tooltip/custom-tooltip.component';
import { CustomTableModelComponent } from '../components/custom-components/custom-table-model/custom-table-model.component';
import { CustomGraphModelComponent } from '../components/custom-components/custom-graph-model/custom-graph-model.component';
import { CustomSourcesComponent } from '../components/custom-components/custom-sources/custom-sources.component';
import { CustomFollowUpSuggestionsComponent } from '../components/custom-components/custom-follow-up-suggestions/custom-follow-up-suggestions.component';
import { CustomMermaidComponent } from '../components/custom-components/custom-mermaid/custom-mermaid.component';

export enum DYNAMIC_COMPONENT {
    TOOLTIP = 'TOOLTIP',
    TABLE_MODEL = 'TABLE_MODEL',
    GRAPH = 'GRAPH',
    GRAPH_MODEL = 'GRAPH_MODEL',
    SOURCES = 'SOURCES',
    FOLLOW_UPS = 'FOLLOW_UPS',
    MERMAID = 'MERMAID',
}

/**
 * Renders components dynamically based on configuration.
 * Usage:
 * make sure to add dynamicComponentRenderer directive to parent if html is sanitized.
 * <div 
 *      class="dynamic-component"
 *      data-component="DYNAMIC_COMPONENT.any">
 * </div>
 */
@Directive({
    selector: '[dynamicComponentRenderer]',
    standalone: true
})
export class DynamicComponentRenderer implements AfterViewInit, OnDestroy {
    private componentRefs: ComponentRef<CustomTooltipComponent>[] = [];
    private customTableModelComponentRefs: ComponentRef<CustomTableModelComponent>[] = [];
    private customGraphModelComponentRefs: ComponentRef<CustomGraphModelComponent>[] = [];
    private customSourcesComponentRefs: ComponentRef<CustomSourcesComponent>[] = [];
    private customFollowUpsComponentRefs: ComponentRef<CustomFollowUpSuggestionsComponent>[] = [];
    private customMermaidComponentRefs: ComponentRef<CustomMermaidComponent>[] = [];

    @Input() disableTooltipNavigation: boolean = false;
    
    private mutationObserver: MutationObserver | null = null;
    constructor(
        private el: ElementRef,
        private viewContainerRef: ViewContainerRef,
    ) { }

    ngAfterViewInit() {
        this.renderComponents();
        this.setupMutationObserver();
    }


    private setupMutationObserver() {
        this.mutationObserver = new MutationObserver(() => {
            this.renderComponents();
        });

        // Observe changes to the host element's children and subtree
        this.mutationObserver.observe(this.el.nativeElement, {
            childList: true,
            subtree: true
        });
    }

    public renderComponents() {
        const elements = this.el.nativeElement.querySelectorAll('.dynamic-component');
        elements.forEach((element: HTMLElement) => {
            const component = element.getAttribute('data-component');
            switch (component) {
                case DYNAMIC_COMPONENT.TOOLTIP:
                    const tooltipContent = element.getAttribute('data-tooltip-content');
                    const tooltipNumber = element.getAttribute('data-tooltip-number');
                    if (!tooltipNumber) break;
                    const componentRef = this.viewContainerRef.createComponent(CustomTooltipComponent);
                    componentRef.instance.content = tooltipContent ?? '';
                    componentRef.instance.number = tooltipNumber;
                    componentRef.instance.disableNavigation = this.disableTooltipNavigation;
                    // Trigger change detection to initialize @ViewChild references
                    componentRef.changeDetectorRef.detectChanges();
                    this.componentRefs.push(componentRef);
                    element.replaceWith(componentRef.location.nativeElement);
                    break;
                case DYNAMIC_COMPONENT.TABLE_MODEL:
                    element.toggleAttribute('data-component')
                    const table = element.children[1].outerHTML;
                    const showOpenButton = element.hasAttribute('data-show-open-button') ? element.getAttribute('data-show-open-button') === 'true' : true;
                    if (!table) break;
                    const tableDialogComponentRef = this.viewContainerRef.createComponent(CustomTableModelComponent);
                    tableDialogComponentRef.instance.table = table;
                    tableDialogComponentRef.instance.showOpenTable = showOpenButton;
                    this.customTableModelComponentRefs.push(tableDialogComponentRef);
                    element.children[0].replaceWith(tableDialogComponentRef.location.nativeElement);
                    break
                case DYNAMIC_COMPONENT.GRAPH_MODEL:
                    const graph = element.getAttribute('data-json-content');
                    element.toggleAttribute('data-json-content')
                    if (!graph) break;
                    const graphDialogComponentRef = this.viewContainerRef.createComponent(CustomGraphModelComponent);
                    graphDialogComponentRef.instance.data = JSON.parse(graph) as any;
                    this.customGraphModelComponentRefs.push(graphDialogComponentRef);
                    element.replaceWith(graphDialogComponentRef.location.nativeElement);
                    break
                case DYNAMIC_COMPONENT.SOURCES:
                    const sourcesData = element.getAttribute('data-sources-content');
                    element.toggleAttribute('data-sources-content');
                    if (!sourcesData) break;
                    try {
                        const sources = JSON.parse(sourcesData);
                        const sourcesComponentRef = this.viewContainerRef.createComponent(CustomSourcesComponent);
                        sourcesComponentRef.instance.sources = sources;
                        this.customSourcesComponentRefs.push(sourcesComponentRef);
                        element.replaceWith(sourcesComponentRef.location.nativeElement);
                    } catch (error) {
                        console.error('Failed to parse sources data:', error);
                    }
                    break
                case DYNAMIC_COMPONENT.FOLLOW_UPS:
                    const followUpsData = element.getAttribute('data-follow-ups-content');
                    element.toggleAttribute('data-follow-ups-content');
                    if (!followUpsData) break;
                    try {
                        const followUps = JSON.parse(followUpsData);
                        const followUpsComponentRef = this.viewContainerRef.createComponent(CustomFollowUpSuggestionsComponent);
                        followUpsComponentRef.instance.followUps = followUps;
                        this.customFollowUpsComponentRefs.push(followUpsComponentRef);
                        element.replaceWith(followUpsComponentRef.location.nativeElement);
                    } catch (error) {
                        console.error('Failed to parse follow-ups data:', error);
                    }
                    break
                case DYNAMIC_COMPONENT.MERMAID:
                    // Source is carried as text inside a hidden <pre>, not a data-* attribute,
                    // so Mermaid's `-->`, `&` and quotes survive intact.
                    const mermaidSource = element.querySelector('.mermaid-source')?.textContent ?? '';
                    if (!mermaidSource.trim()) break;
                    // The message innerHTML is rebuilt on every streaming chunk, so diagrams
                    // that close mid-message get re-created repeatedly. Drop the refs whose
                    // host element is already detached, or they accumulate until the message
                    // is destroyed.
                    this.pruneDetachedMermaidRefs();
                    const mermaidComponentRef = this.viewContainerRef.createComponent(CustomMermaidComponent);
                    mermaidComponentRef.instance.code = mermaidSource;
                    // Render before swapping in, so a cached diagram never paints as an
                    // empty box for a frame (same reason TOOLTIP calls detectChanges).
                    mermaidComponentRef.changeDetectorRef.detectChanges();
                    this.customMermaidComponentRefs.push(mermaidComponentRef);
                    element.replaceWith(mermaidComponentRef.location.nativeElement);
                    break

                default:
            }
        });
    }

    /** Destroys mermaid components whose host element has been removed from the DOM. */
    private pruneDetachedMermaidRefs() {
        this.customMermaidComponentRefs = this.customMermaidComponentRefs.filter(ref => {
            if (ref.location.nativeElement.isConnected) return true;
            ref.destroy();
            return false;
        });
    }

    ngOnDestroy() {
        this.componentRefs.forEach(ref => ref.destroy());
        this.customTableModelComponentRefs.forEach(ref => ref.destroy());
        this.customGraphModelComponentRefs.forEach(ref => ref.destroy());
        this.customSourcesComponentRefs.forEach(ref => ref.destroy());
        this.customFollowUpsComponentRefs.forEach(ref => ref.destroy());
        this.customMermaidComponentRefs.forEach(ref => ref.destroy());
        this.customGraphModelComponentRefs = [];
        this.customTableModelComponentRefs = [];
        this.componentRefs = [];
        this.customSourcesComponentRefs = [];
        this.customFollowUpsComponentRefs = [];
        this.customMermaidComponentRefs = [];
    }
}